import React, { useCallback } from 'react';

import { Select } from '@aline/common';
import { Skeleton } from '@mui/material';
import { File } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip } from 'src/components';
import { CommentsIcon } from 'src/components/icons/CommentsIcon';
import { PeopleSmallIcon } from 'src/components/icons/PeopleSmallIcon';
import { Apollo } from 'src/gql/generated';
import { useWorkflowActivity } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import { useSidePanelView } from './side-panel/use-side-panel-view';
import type { SidePanelView } from './side-panel/use-side-panel-view';
import { useWorkflowDetailLinks } from './WorkflowDetailContext';

const useStyles = makeStyles()(theme => ({
  card: {
    '&:hover': {
      borderColor: theme.appColors.primary,
      boxShadow: '0px 1px 8.2px -2px rgba(0, 0, 0, 0.15)',
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1.5),
    minWidth: 0,
    padding: theme.spacing(1.5, 2),
    transition: 'border-color 150ms, box-shadow 150ms',
  },
  count: {
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 'bold',
  },
  ctaText: {
    color: theme.appColors.primary,
    fontSize: 12,
    fontWeight: 600,
  },
  iconCircle: {
    alignItems: 'center',
    backgroundColor: 'rgba(20, 110, 232, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  iconInCircle: {
    color: theme.appColors.primary,
    height: 16,
    width: 16,
  },
  label: {
    color: 'rgba(50, 74, 82, 0.6)',
    fontSize: 12,
  },
  root: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  textBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
}));

type WorkflowPermission = NonNullable<
  ReturnType<typeof useWorkflowDetailLinks>['workflow']
>['permissions'][number];

interface SummaryCardProps {
  count: number;
  emptyCta: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  onClick: () => void;
  dataCy?: string;
  /**
   * The count failed to load: show an em-dash instead of a number (a 0 would read as a genuinely
   * empty feed) and explain why in a tooltip.
   */
  errorTooltip?: string;
}

function SummaryCard({
  count,
  dataCy,
  emptyCta,
  errorTooltip,
  icon: Icon,
  label,
  onClick,
}: SummaryCardProps): JSX.Element {
  const { classes } = useStyles();
  const hasError = Boolean(errorTooltip);
  const isEmpty = !hasError && count === 0;

  return (
    <AlineTooltip title={errorTooltip ?? ''}>
      <div className={classes.card} data-cy={dataCy} role="button" tabIndex={0} onClick={onClick}>
        <div className={classes.iconCircle}>
          <Icon className={classes.iconInCircle} style={{ height: 16, width: 16 }} />
        </div>
        <div className={classes.textBlock}>
          {isEmpty ? (
            <span className={classes.ctaText}>{emptyCta}</span>
          ) : (
            <>
              <span className={classes.count}>{hasError ? '—' : count}</span>
              <span className={classes.label}>{label}</span>
            </>
          )}
        </div>
      </div>
    </AlineTooltip>
  );
}

function SummaryCardSkeleton() {
  const { classes } = useStyles();
  return (
    <div className={classes.card}>
      <Skeleton height={32} variant="circular" width={32} />
      <div className={classes.textBlock}>
        <Skeleton height={16} variant="text" width={32} />
        <Skeleton height={12} variant="text" width={64} />
      </div>
    </div>
  );
}

interface WorkflowSummaryCardsProps {
  workflowId: string;
}

export const WorkflowSummaryCards = React.memo(function WorkflowSummaryCards({
  workflowId,
}: WorkflowSummaryCardsProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { view } = useSidePanelView();
  const { openDrawer } = useGlobalDrawer();
  const links = useWorkflowDetailLinks(workflowId);

  /**
   * The "Activity" card opens a drawer that shows the full activity feed (comments + status
   * rows), so count from that feed's total rather than `comments.length`. The hook is live by
   * default, so the count refreshes off the push-invalidation signal (a refetch only when
   * something actually changed) and shares Apollo's cache with the open drawer; skipped for
   * templates, which don't render the card.
   */
  const {
    error: activityCountError,
    loading: activityCountLoading,
    totalCount: activityCount,
  } = useWorkflowActivity(workflowId, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    skip: links.workflow?.state === Apollo.WorkflowState.Template,
  });

  const getPanelPath = useCallback(
    (panelView: SidePanelView) => {
      switch (panelView) {
        case 'documents':
          return links.documents();
        case 'comments':
          return links.comments();
        case 'collaborators':
          return links.collaborators();
        default:
          return links.baseUrl();
      }
    },
    [links]
  );

  const handleCardClick = useCallback(
    (panelView: SidePanelView) => {
      if (view === panelView) {
        navigate(links.baseUrl());
      } else {
        openDrawer();
        navigate(getPanelPath(panelView));
      }
    },
    [getPanelPath, links, navigate, openDrawer, view]
  );

  if (!links.workflow) {
    return (
      <div className={classes.root}>
        <SummaryCardSkeleton />
        <SummaryCardSkeleton />
        <SummaryCardSkeleton />
      </div>
    );
  }

  const visibleCollaboratorCount =
    links.workflow.permissions.filter(getIsVisibleCollaborator).length;

  return (
    <div className={classes.root}>
      <SummaryCard
        count={links.workflow.files.length}
        dataCy={Select.workflow.detail.summaryCard.documents}
        emptyCta="Add document"
        icon={File}
        label="Documents"
        onClick={() => handleCardClick('documents')}
      />
      {links.workflow.state !== Apollo.WorkflowState.Template &&
        (activityCountLoading && !activityCount && !activityCountError ? (
          // Don't assert an empty feed before the count's first read resolves.
          <SummaryCardSkeleton />
        ) : (
          <SummaryCard
            count={activityCount}
            emptyCta="Start a discussion"
            errorTooltip={activityCountError ? "Couldn't load activity" : undefined}
            icon={CommentsIcon}
            label="Activity"
            onClick={() => handleCardClick('comments')}
          />
        ))}
      <SummaryCard
        count={visibleCollaboratorCount}
        emptyCta="Add collaborators"
        icon={PeopleSmallIcon}
        label="Collaborators"
        onClick={() => handleCardClick('collaborators')}
      />
    </div>
  );
});

function getIsVisibleCollaborator(permission: WorkflowPermission): boolean {
  return Boolean(permission.account || permission.role);
}
